import type { FooterColumnLinks, LinkItem } from "~~/models/links";
import type { CustomLinkRecord, FooterColumnRecord, ImageLinkRecord, PageLinkRecord } from "~~/server/graphql/datocms/generated";
import { stripStega } from "vue-datocms";
import { i18nConfig } from "~~/i18n/i18n-nuxt.config";

export function createFooterColumnLinks(data: Array<FooterColumnRecord>, localePath: (path: string) => string): Array<FooterColumnLinks> {
  return data.map((column) => {
    return {
      id: column.id,
      label: column.title,
      children: column.links.map((link) => {
        if (link._modelApiKey === "custom_link") {
          const customLink = link as CustomLinkRecord;
          const customUrl = stripStega(customLink.customLink);

          return {
            id: customLink.id,
            label: customLink.text,
            to: customUrl,
          };
        }
        if (link._modelApiKey === "page_link") {
          const pageLink = link as PageLinkRecord;
          const pageUrl = getUrl(pageLink.pageLink!.slug, localePath);

          return {
            id: pageLink.id,
            label: pageLink.text,
            to: pageUrl,
          };
        }

        return { id: "0", label: `Unknown link type ${link._modelApiKey}`, to: "#" };
      }),
    } satisfies FooterColumnLinks;
  });
}

export function getLinkForType(link: any, localePath: (path: string) => string): LinkItem {
  if (!link) {
    return { id: "0", label: `Unknown link` };
  }

  if (link?._modelApiKey === "custom_link") {
    const customLink = link as CustomLinkRecord;
    const customUrl = stripStega(customLink.customLink || "/");

    return {
      id: customLink.id,
      label: customLink.text,
      to: customUrl,
      target: getTarget(customUrl),
    };
  }

  if (link?._modelApiKey === "page_link") {
    const pageLink = link as PageLinkRecord;
    const pageUrl = getUrl(pageLink.pageLink!.slug, localePath);

    return {
      id: pageLink.id,
      label: pageLink.text,
      to: pageUrl,
      target: getTarget(pageUrl),
    };
  }

  return { id: "0", label: `Unknown link type ${link._modelApiKey}` };
}

export function getTarget(path: string) {
  try {
    if (path.startsWith("http")) {
      const pathUrl = new URL(path);
      const requestUrl = useRequestURL();

      if (pathUrl.host !== requestUrl.host) {
        return "_blank";
      }
    }

    return "_self";
  }
  catch {
    return "_blank";
  }
}

export function getImageLinkTarget(imageLink: ImageLinkRecord, localePath: (path: string) => string) {
  return getTarget(getImageLinkUrl(imageLink, localePath));
}

export function getUrl(path: string, localePath: (path: string) => string) {
  // strips the stega from the path, which is used while using the visual editing feature of DatoCMS
  const cleanPath = stripStega(path);

  // external URLs (with http/https)
  if (cleanPath.indexOf(":") > 0) {
    return cleanPath;
  }

  // protocol-relative external URLs
  if (cleanPath.startsWith("//")) {
    return cleanPath;
  }

  // internal URLs (with locale prefix)
  if (i18nConfig.locales.some(locale => cleanPath.startsWith(`/${locale.code}`))) {
    return cleanPath;
  }

  // internal URLs (with leading slash)
  if (cleanPath.startsWith("/")) {
    return localePath(cleanPath);
  }

  // internal URLs (without leading slash)
  return localePath(`/${cleanPath}`);
}

export function getImageLinkUrl(imageLink: ImageLinkRecord, localePath: (path: string) => string) {
  if (imageLink.optPageLink) {
    return getUrl(imageLink.optPageLink!.slug, localePath);
  }

  if (imageLink.optCustomLink) {
    return getUrl(imageLink.optCustomLink, localePath);
  }

  return "/";
}
